/**
 * email_queue + email_events row schemas.
 *
 * Mirrors supabase/migrations/0015_email_and_invites.sql and the
 * EmailDataByTypeSchema in @sazeo/email, but kept here so the shared
 * `@sazeo/schemas` package stays the single source of DB-row truth.
 */

import { z } from 'zod';
import { EmailSchema, LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

export const EMAIL_TYPES = [
  'auth.magic_link',
  'auth.password_reset',
  'auth.email_changed',
  'invite.tenant_member',
  'notification.mention',
  'notification.assignment',
  'notification.digest',
  'billing.invoice_issued',
  'billing.payment_failed',
  'support.ticket_reply',
] as const;
export const EmailTypeSchema = z.enum(EMAIL_TYPES);
export type EmailType = z.infer<typeof EmailTypeSchema>;

export const EmailQueueStatusSchema = z.enum([
  'pending',
  'sending',
  'sent',
  'bounced',
  'complained',
  'failed',
]);
export type EmailQueueStatus = z.infer<typeof EmailQueueStatusSchema>;

export const EmailQueueRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema.nullable(),
  type: EmailTypeSchema,
  to_email: EmailSchema,
  from_email: z.string(),
  locale: LangCodeSchema,
  data: z.record(z.unknown()),
  status: EmailQueueStatusSchema,
  attempts: z.number().int().nonnegative(),
  last_error: z.string().nullable(),
  resend_id: z.string().nullable(),
  scheduled_at: TimestampSchema,
  sent_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type EmailQueueRow = z.infer<typeof EmailQueueRowSchema>;

export const EmailEventKindSchema = z.enum([
  'email.sent',
  'email.delivered',
  'email.delivery_delayed',
  'email.bounced',
  'email.complained',
  'email.opened',
  'email.clicked',
  'email.failed',
]);
export type EmailEventKind = z.infer<typeof EmailEventKindSchema>;

export const EmailEventRowSchema = z.object({
  id: UuidSchema,
  resend_id: z.string(),
  kind: EmailEventKindSchema,
  payload: z.record(z.unknown()),
  received_at: TimestampSchema,
});
export type EmailEventRow = z.infer<typeof EmailEventRowSchema>;

// User email prefs (stored as jsonb on users.email_prefs).
export const UserEmailPrefsSchema = z.object({
  digest_daily: z.boolean().default(false),
  mentions: z.boolean().default(true),
  assignments: z.boolean().default(true),
  documents: z.boolean().default(true),
  invites: z.boolean().default(true),
  billing: z.boolean().default(true),
  marketing: z.boolean().default(false),
});
export type UserEmailPrefs = z.infer<typeof UserEmailPrefsSchema>;
